import { matchPath, useLocation, useNavigate } from 'react-router';

import Collapse from '@mui/material/Collapse';

import NavItem from './NavItem';

import type { NavListProps, NavSubListProps } from './types';

export function useActiveLink(path: string, deep = true): boolean {
  const { pathname } = useLocation();

  const normalActive = path ? !!matchPath({ path, end: true }, pathname) : false;

  const deepActive = path ? !!matchPath({ path, end: false }, pathname) : false;

  return deep ? deepActive : normalActive;
}

// ----------------------------------------------------------------------

export default function NavList({ data, depth, slotProps }: NavListProps) {
  const active = useActiveLink(data.path, !!data.children);

  const navigate = useNavigate();

  const hasChildren = !!data.children;

  const handleClick = () => {
    // Only navigate using navigate() for items with children
    // Items without children use the RouterLink (which prevents double navigation)
    if (hasChildren) {
      navigate(data.path);
    }
  };

  return (
    <>
      <NavItem
        onClick={handleClick}
        //
        title={data.title}
        path={data.path}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={hasChildren}
        externalLink={data.path.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        sx={{
          mb: `${slotProps?.gap}px`,
          ...(depth === 1 ? slotProps?.rootItem : slotProps?.subItem),
        }}
      />

      {!!data.children && (
        <Collapse in>
          <NavSubList data={data.children} depth={depth} slotProps={slotProps} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({ data, depth, slotProps }: NavSubListProps) {
  return (
    <>
      {data.map((list) => (
        <NavList key={list.title} data={list} depth={depth + 1} slotProps={slotProps} />
      ))}
    </>
  );
}
